import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { format } from 'date-fns';

import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/SossietyDialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Training } from '@/types/trainings';
import { useAuth } from '@/contexts/AuthContext';
import { SossietyDialogHeader, SossietyDialogClose } from '@/components/ui/SossietyDialogHeader';
import { supabase } from '@/integrations/supabase/client';
import { ToggleSelector } from '@/components/ui/ToggleSelector';
import { DeleteConfirmationModal } from '@/components/ui/DeleteConfirmationModal';
import { PartnerDisplay } from '@/components/ui/PartnerDisplay';
import { useToast } from '@/hooks/use-toast';
import { useI18n } from '@/contexts/I18nContext';
import { formatDateTimeForDisplay } from '@/utils/timezoneUtils';

interface ClassViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  classData: Training | null;
  notesData?: any;
  onStatusUpdate?: (classId: string, status: 'Completed' | 'Missed') => void;
  onOpenNotesModal?: (classData: Training) => void;
  onEditClass?: (classData: Training) => void;
  onClassDeleted?: (classId: string) => void;
  onClassUpdated?: () => void;
  refreshKey?: number;
}

export const ClassViewModal = forwardRef<
  { forceReload: () => Promise<void> },
  ClassViewModalProps
>(({ isOpen, onClose, classData, notesData, onStatusUpdate, onOpenNotesModal, onEditClass, onClassDeleted, onClassUpdated, refreshKey = 0 }, ref) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useI18n();
  const [currentStatus, setCurrentStatus] = useState<string>('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [classAcademy, setClassAcademy] = useState<string>('');
  const [classStudents, setClassStudents] = useState<Array<{name: string, id?: string, belt_rank?: string}>>([]);

  // Get notes data for this class
  const classNotesData = notesData?.[classData?.id || ''];
  const hasNotes = !!classNotesData?.notes;




  // Update current status when classData changes
  useEffect(() => {
    if (classData) {
      setCurrentStatus(classData.status || 'Scheduled');
    }
  }, [classData]);

  // Force reload when refreshKey changes
  useEffect(() => {
    if (refreshKey > 0) {
      // Force a re-render when refreshKey changes
      // This ensures the modal shows updated data
    }
  }, [refreshKey]);

  // Fetch class academy
  useEffect(() => {
    const fetchClassAcademy = async () => {
      // First try to use academy_name if available
      if (classData?.academy_name) {
        setClassAcademy(classData.academy_name);
        return;
      }
      
      // If no academy_name, try to fetch by academy_id
      if (!classData?.academy_id) {
        setClassAcademy('');
        return;
      }
      
      try {
        const { data: academy, error } = await supabase
          .from('academies')
          .select('name')
          .eq('id', classData.academy_id)
          .single();

        if (error) {
          console.error('Error fetching class academy:', error);
          setClassAcademy('');
          return;
        }

        setClassAcademy(academy?.name || '');
      } catch (error) {
        console.error('Error fetching class academy:', error);
        setClassAcademy('');
      }
    };

    fetchClassAcademy();
  }, [classData?.academy_id, classData?.academy_name]);

  // Fetch class students
  useEffect(() => {
    const fetchClassStudents = async () => {
      
      if (!classData?.id) {
        setClassStudents([]);
        return;
      }
      
      try {
        const { data: partners, error } = await supabase
          .from('training_partners')
          .select(`
            manual_partner_name,
            partner_user_id,
            profiles(full_name, belt_rank)
          `)
          .eq('training_id', classData.id);

        if (error) {
          console.error('Error fetching class students:', error);
          setClassStudents([]);
          return;
        }

        
        const students = partners?.map(partner => ({
          name: partner.manual_partner_name || partner.profiles?.full_name || 'Unknown',
          id: partner.partner_user_id,
          belt_rank: partner.profiles?.belt_rank || 'white' // Default to white if no belt rank
        })) || [];

        setClassStudents(students);
      } catch (error) {
        console.error('Error fetching class students:', error);
        setClassStudents([]);
      }
    };

    fetchClassStudents();
  }, [classData?.id, refreshKey]);

  // Force reload function exposed via ref
  const forceReload = useCallback(async () => {
    if (!classData?.id) return;
    
    try {
      // Simulate a small delay to show shimmer
      await new Promise(resolve => setTimeout(resolve, 500));
      
    } catch (error) {
      console.error('🔄 ClassViewModal - Error reloading data:', error);
    }
  }, [classData?.id]);

  // Expose forceReload via ref
  useImperativeHandle(ref, () => ({
    forceReload
  }), [forceReload]);

  const handleStatusUpdate = async (newStatus: 'Completed' | 'Missed') => {
    if (!classData?.id) return;

    setIsUpdatingStatus(true);
    try {
      // Here you would call the API to update the status
      // For now, just update local state
      setCurrentStatus(newStatus);
      
      if (onStatusUpdate) {
        onStatusUpdate(classData.id, newStatus);
      }

      toast({
        title: "Status Updated",
        description: `Class marked as ${newStatus.toLowerCase()}`,
        duration: 2000,
      });
    } catch (error) {
      console.error('Error updating class status:', error);
      toast({
        title: "Error",
        description: "Failed to update class status",
        variant: "destructive",
        duration: 2000,
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleOpenNotes = () => {
    if (onOpenNotesModal && classData) {
      onOpenNotesModal(classData);
    }
  };

  const handleEditClick = () => {
    if (onEditClass && classData) {
      onEditClass(classData);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  // Check if class is in the past
  const isPastClass = () => {
    if (!classData?.start_time) return false;
    const classTime = new Date(classData.start_time);
    const now = new Date();
    return classTime < now;
  };

  // Render techniques function
  const renderTechniques = (techniques: any[]) => {
    if (!techniques || techniques.length === 0) {
      return null;
    }

    return (
      <div className="flex flex-wrap gap-1">
        {techniques.map((technique, index) => {
          // Handle different data structures
          let techniqueName = '';
          if (typeof technique === 'string') {
            techniqueName = technique;
          } else if (technique?.name) {
            techniqueName = technique.name;
          } else if (technique?.id) {
            // Try to get name from technique ID
            techniqueName = `Technique ${technique.id}`;
          } else {
            techniqueName = `Technique ${index + 1}`;
          }
          
          return (
            <span
              key={index}
              className="px-2 py-1 bg-[#D72E16] text-white rounded-full text-xs font-medium"
            >
              {techniqueName}
            </span>
          );
        })}
      </div>
    );
  };

  const handleDeleteConfirm = async () => {
    if (!classData?.id) return;

    setIsDeleting(true);
    try {
      // Call the parent's delete handler with the class ID
      if (onClassDeleted) {
        await onClassDeleted(classData.id);
      }
      
      toast({
        title: "Class Deleted",
        description: "The class has been successfully deleted.",
        duration: 2000,
      });
      
      onClose();
    } catch (error) {
      console.error('Error deleting class:', error);
      toast({
        title: "Error",
        description: "Failed to delete class",
        variant: "destructive",
        duration: 2000,
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };



  if (!classData) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="w-[90vw] max-w-md bg-white rounded-2xl p-6">
          <DialogTitle className="sr-only">{t('calendar.class_details')}</DialogTitle>
          <DialogDescription id="class-details-description" className="sr-only">
            View and manage class session details, notes, and students
          </DialogDescription>
          <SossietyDialogHeader
            title={t('calendar.class_details')}
            titleClassName="text-lg"
          >
            <SossietyDialogClose onClick={onClose} />
          </SossietyDialogHeader>

          {/* Status Selector Buttons - Show for all classes */}
          <ToggleSelector
            label="Status"
            options={[
              { value: 'Completed', label: t('calendar.completed'), color: 'green' },
              { value: 'Missed', label: t('calendar.missed'), color: 'red' }
            ]}
            value={currentStatus}
            onChange={(value) => handleStatusUpdate(value as 'Completed' | 'Missed')}
            disabled={isUpdatingStatus}
            className="mb-2"
          />

          <div className="space-y-2 max-h-[75vh] overflow-y-auto pr-2">
            {/* Class Type and Modality */}
            <div className="flex items-center justify-between">
              <span className="text-black font-semibold" style={{ fontSize: '16px', fontWeight: 600 }}>
                {classData.type || t('calendar.group_class')} - {classData.modality}
              </span>
            </div>

            {/* Class Category */}
            {classData.category && (
              <div className="!mt-0">
                <span className="text-gray-600 font-medium text-sm">
                  {classData.category}
                </span>
              </div>
            )}

            {/* Date and Time */}
            <div className="!mt-0 !mb-3">
              <p className="text-gray-500 font-sans text-xs">
                {format(new Date(classData.start_time), 'MMMM d, yyyy')} - {formatDateTimeForDisplay(classData.start_time).time} to {formatDateTimeForDisplay(classData.end_time).time}
              </p>
            </div>

            {/* Location */}
            <div className="!mb-3">
              <label className="block text-sm font-semibold text-black">{t('profile.location')}</label>
              <p className="text-sm text-gray-500">
                {classAcademy || t('calendar.no_location_specified')}
              </p>
            </div>

            {/* Students */}
            <div className="!mb-3">
              <label className="block text-sm font-semibold text-black">{t('calendar.students')}</label>
              {classStudents.length > 0 ? (
                <div className="space-y-2">
                  {classStudents.map((student, index) => (
                    <PartnerDisplay
                      key={student.id || index}
                      partner={{
                        id: student.id || `student-${index}`,
                        partner_name: student.name,
                        partner_username: undefined,
                        partner_avatar_url: undefined,
                        partner_belt_rank: student.belt_rank,
                        is_manual: true
                      }}
                      showRemoveButton={false}
                      size="md"
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">{t('calendar.no_students_registered')}</p>
              )}
            </div>

            {/* Class Notes - Only show for Completed or Missed status */}
            {classData.status !== 'Scheduled' && (
              <div className="space-y-2 !mb-3">
                <div className="flex items-center justify-between">
                  <h3 className="block text-sm font-semibold text-gray-900">{t('calendar.class_notes')}</h3>
                  <button
                    onClick={handleOpenNotes}
                    className="text-red-600 underline text-sm font-medium"
                  >
                    {hasNotes ? t('calendar.review_notes') : t('calendar.add_notes')}
                  </button>
                </div>
              </div>
            )}

            {/* Techniques Section - Only show for Completed or Missed status */}
            {classData.status !== 'Scheduled' && classNotesData?.techniques && classNotesData.techniques.length > 0 && (
              <div className="space-y-2 !mb-3">
                <h3 className="block text-sm font-semibold text-gray-900">{t('calendar.techniques_label')}</h3>
                {renderTechniques(classNotesData.techniques)}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <Button
                onClick={handleDeleteClick}
                variant="outline"
                className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-full"
              >
                Delete
              </Button>
              
              <Button
                onClick={handleEditClick}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full"
              >
                {t('common.edit')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
        title={t('common.delete') + ' ' + t('calendar.class').toLowerCase()}
        message="Are you sure you want to delete this class? This action cannot be undone."
        isLoading={isDeleting}
      />
    </>
  );
});

ClassViewModal.displayName = 'ClassViewModal';
